import React, { useState } from "react";
import { Palette, Edit2, Save, X, Plus, Trash2 } from "lucide-react";

/**
 * Reusable Color Picker Component
 * Manages brand colors with categories (body, h1, h2, link, button, etc.)
 */
export default function ColorPicker({ colors = {}, onChange, title = "Brand Colors", readOnly = false }) {
  const [editingCategory, setEditingCategory] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addingNew, setAddingNew] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    text_color: "#000000",
    background_color: "#ffffff",
    border_color: "#000000",
  });

  const handleEdit = (category) => {
    setEditingCategory(category);
    const colorSet = colors[category] || {};
    setEditForm({
      text_color: colorSet.text_color || "#000000",
      background_color: colorSet.background_color || "#ffffff",
      border_color: colorSet.border_color || "#000000",
    });
  };

  const handleSave = () => {
    onChange({
      ...colors,
      [editingCategory]: editForm,
    });
    setEditingCategory(null);
    setEditForm({});
  };

  const handleCancel = () => {
    setEditingCategory(null);
    setEditForm({});
  };

  const handleRemove = (category) => {
    const updated = { ...colors };
    delete updated[category];
    onChange(updated);
  };

  const handleAddNew = () => {
    if (newCategory.name.trim()) {
      const categoryKey = newCategory.name.toLowerCase().replace(/\s+/g, "_");
      onChange({
        ...colors,
        [categoryKey]: {
          text_color: newCategory.text_color,
          background_color: newCategory.background_color,
          border_color: newCategory.border_color,
        },
      });
      setNewCategory({
        name: "",
        text_color: "#000000",
        background_color: "#ffffff",
        border_color: "#000000",
      });
      setAddingNew(false);
    }
  };

  const handleCancelAdd = () => {
    setNewCategory({
      name: "",
      text_color: "#000000",
      background_color: "#ffffff",
      border_color: "#000000",
    });
    setAddingNew(false);
  };

  const ColorInput = ({ label, value, onChange }) => (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="#000000"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {title} ({Object.keys(colors).length} categories)
        </h3>
        {!readOnly && (
          <button
            onClick={() => setAddingNew(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </button>
        )}
      </div>

      {Object.keys(colors).length === 0 && !addingNew ? (
        <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <Palette className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg mb-2">No colors defined yet</p>
          <p className="text-sm">Click "Add Category" to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(colors)
            .filter(([category, colorSet]) => colorSet != null && typeof colorSet === 'object')
            .map(([category, colorSet]) => {
              // Ensure colorSet has default values if properties are missing
              const safeColorSet = {
                text_color: colorSet?.text_color || "#000000",
                background_color: colorSet?.background_color || "#ffffff",
                border_color: colorSet?.border_color || "#000000",
              };
              
              return (
                <div
                  key={category}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                >
                  {editingCategory === category ? (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900 capitalize mb-3">
                        {category.replace(/_/g, " ")}
                      </h4>
                      <ColorInput
                        label="Text Color"
                        value={editForm.text_color || "#000000"}
                        onChange={(val) =>
                          setEditForm({ ...editForm, text_color: val })
                        }
                      />
                      <ColorInput
                        label="Background Color"
                        value={editForm.background_color || "#ffffff"}
                        onChange={(val) =>
                          setEditForm({ ...editForm, background_color: val })
                        }
                      />
                      <ColorInput
                        label="Border Color"
                        value={editForm.border_color || "#000000"}
                        onChange={(val) =>
                          setEditForm({ ...editForm, border_color: val })
                        }
                      />
                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={handleSave}
                          className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center justify-center gap-1"
                        >
                          <Save className="w-3 h-3" />
                          Save
                        </button>
                        <button
                          onClick={handleCancel}
                          className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 flex items-center justify-center gap-1"
                        >
                          <X className="w-3 h-3" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 capitalize">
                          {category.replace(/_/g, " ")}
                        </h4>
                        {!readOnly && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(category)}
                              className="p-1.5 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                            >
                              <Edit2 className="w-3.5 h-3.5 text-gray-600" />
                            </button>
                            <button
                              onClick={() => handleRemove(category)}
                              className="p-1.5 bg-gray-100 rounded hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-600" />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded border border-gray-300"
                            style={{ backgroundColor: safeColorSet.text_color }}
                          />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Text</p>
                            <p className="text-xs font-mono text-gray-700">
                              {safeColorSet.text_color}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded border border-gray-300"
                            style={{ backgroundColor: safeColorSet.background_color }}
                          />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Background</p>
                            <p className="text-xs font-mono text-gray-700">
                              {safeColorSet.background_color}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded border border-gray-300"
                            style={{ backgroundColor: safeColorSet.border_color }}
                          />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Border</p>
                            <p className="text-xs font-mono text-gray-700">
                              {safeColorSet.border_color}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

          {/* Add New Category Card */}
          {addingNew && (
            <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 space-y-3 bg-blue-50">
              <h4 className="text-sm font-semibold text-gray-900">New Category</h4>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, name: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Primary Button"
                />
              </div>
              <ColorInput
                label="Text Color"
                value={newCategory.text_color}
                onChange={(val) =>
                  setNewCategory({ ...newCategory, text_color: val })
                }
              />
              <ColorInput
                label="Background Color"
                value={newCategory.background_color}
                onChange={(val) =>
                  setNewCategory({ ...newCategory, background_color: val })
                }
              />
              <ColorInput
                label="Border Color"
                value={newCategory.border_color}
                onChange={(val) =>
                  setNewCategory({ ...newCategory, border_color: val })
                }
              />
              <div className="flex gap-2">
                <button
                  onClick={handleAddNew}
                  disabled={!newCategory.name.trim()}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add
                </button>
                <button
                  onClick={handleCancelAdd}
                  className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 flex items-center justify-center gap-1"
                >
                  <X className="w-3 h-3" />
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

